<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();

const apiKey = ref('');
const apiEndpoint = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const showConfigModal = ref(false);

const connectedHook = computed(() => {
  const hooks = store.getters['integrations/getIntegrationHooks'];
  return hooks?.find(h => h.app_id === 'cetakers_flow') || null;
});

const isConnected = computed(() => !!connectedHook.value);

const maskedKey = computed(() => {
  if (!connectedHook.value?.settings?.api_key) return '';
  const key = connectedHook.value.settings.api_key;
  return key.length > 8 ? `${key.slice(0, 4)}${'*'.repeat(key.length - 8)}${key.slice(-4)}` : '****';
});

onMounted(async () => {
  await store.dispatch('integrations/get');
  await store.dispatch('integrations/fetchHooks', { app_id: 'cetakers_flow' });
});

const openConfigModal = () => {
  showConfigModal.value = true;
};

const closeConfigModal = () => {
  showConfigModal.value = false;
  apiKey.value = '';
  apiEndpoint.value = '';
};

const handleConnect = async () => {
  if (!apiKey.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'cetakers_flow',
      settings: { 
        api_key: apiKey.value.trim(),
        api_endpoint: apiEndpoint.value.trim() || undefined,
      },
    });
    useAlert('Cetakers Flow berhasil disambungkan.');
    closeConfigModal();
  } catch {
    useAlert('Gagal menyambungkan Cetakers Flow. Periksa API Key Anda dan coba lagi.');
  } finally {
    isSaving.value = false;
  }
};

const handleDisconnect = async () => {
  if (!connectedHook.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm('Apakah Anda yakin ingin memutus sambungan Cetakers Flow? Fitur dokumen dan menu Cetakers Flow akan dinonaktifkan.')) return;
  isSaving.value = true;
  try {
    await store.dispatch('integrations/deleteHook', connectedHook.value.id);
    useAlert('Cetakers Flow berhasil diputus.');
  } catch {
    useAlert('Gagal memutus sambungan Cetakers Flow.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Cetakers Flow"
        description=""
        back-button-label="Integrations"
      />
    </template>
    
    <template #body>
      <div class="w-full">
        <div class="outline outline-n-container outline-1 bg-n-card rounded-xl flex-grow overflow-auto p-4">
          <div class="flex items-center justify-center">
            <div class="flex h-16 w-16 items-center justify-center">
              <img
                :src="'/dashboard/images/integrations/cetakers_flow.png'"
                alt="Cetakers Flow"
                class="max-w-full rounded-md border border-n-weak shadow-sm block bg-n-alpha-3 dark:bg-n-alpha-2"
              />
            </div>
            <div class="flex flex-col justify-center m-0 mx-4 flex-1">
              <h3 class="mb-1 text-heading-1 text-n-slate-12">
                Cetakers Flow
              </h3>
              <p class="text-n-slate-11 text-body-main">
                Cetakers Flow adalah sistem manajemen operasional percetakan. Dengan integrasi ini, Anda dapat menghubungkan data dari Cetakers Pro langsung ke dalam Chatwoot, seperti melihat data Invoice dan SPK pelanggan langsung di panel obrolan.
              </p>
            </div>
            <div class="flex justify-center items-center mb-0 w-[15%]">
              <div v-if="isConnected">
                <Button
                  color="ruby"
                  variant="faded"
                  :is-loading="isSaving"
                  @click="handleDisconnect"
                >
                  Putuskan Sambungan
                </Button>
              </div>
              <div v-else>
                <Button
                  color="blue"
                  variant="faded"
                  @click="openConfigModal"
                >
                  Sambungkan
                </Button>
              </div>
            </div>
          </div>
          
          <!-- Additional connected info if needed, shown below the horizontal bar -->
          <div v-if="isConnected" class="mt-6 border-t border-n-weak pt-4">
            <h4 class="text-sm font-semibold text-n-slate-12 mb-2">Konfigurasi Aktif</h4>
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">API KEY</span>
                <span class="font-mono text-sm text-n-slate-12">{{ maskedKey }}</span>
              </div>
              <div class="flex flex-col gap-1" v-if="connectedHook?.settings?.api_endpoint">
                <span class="text-xs font-medium text-n-slate-11">API ENDPOINT</span>
                <span class="font-mono text-sm text-n-slate-12">{{ connectedHook.settings.api_endpoint }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal Konfigurasi -->
    <woot-modal v-model:show="showConfigModal" :on-close="closeConfigModal">
      <woot-modal-header
        header-title="Konfigurasi API Cetakers Flow"
        header-content="Masukkan kredensial API untuk mengaktifkan fitur ini."
      />
      <div class="p-6">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <label
              for="cetakers-api-key"
              class="text-sm font-medium text-n-slate-12"
            >
              API Key <span class="text-n-ruby-9">*</span>
            </label>
            <input
              id="cetakers-api-key"
              v-model="apiKey"
              type="text"
              placeholder="Masukkan API Key Cetakers Flow Anda"
              class="w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-w-brand-primary"
            />
          </div>
          
          <div class="flex flex-col gap-1.5">
            <label
              for="cetakers-endpoint"
              class="text-sm font-medium text-n-slate-12"
            >
              API Endpoint URL (Opsional)
            </label>
            <input
              id="cetakers-endpoint"
              v-model="apiEndpoint"
              type="text"
              placeholder="https://api.cetakers.com/v1"
              class="w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-w-brand-primary"
            />
            <span class="text-xs text-n-slate-11">Kosongkan jika menggunakan endpoint default.</span>
          </div>
        </div>
        
        <div class="flex justify-end gap-3 mt-8">
          <Button
            variant="ghost"
            color="slate"
            @click="closeConfigModal"
          >
            Batal
          </Button>
          <Button
            color="blue"
            :is-loading="isSaving"
            :disabled="!apiKey.trim()"
            @click="handleConnect"
          >
            Simpan & Sambungkan
          </Button>
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
